import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './AuthModal.css';

export default function AuthModal() {
  const {
    authState,
    error,
    showAuthModal,
    handleSignIn,
    handleSignUp,
    handleConfirmSignUp,
    handleResendCode,
    handleAppleSignIn,
    closeAuthModal,
    switchToSignUp,
    switchToSignIn,
  } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!showAuthModal) {
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setCode('');
      setLocalError('');
      setSubmitting(false);
    }
  }, [showAuthModal]);

  if (!showAuthModal) return null;

  const onSignIn = async (e) => {
    e.preventDefault();
    setLocalError('');
    setSubmitting(true);
    try {
      await handleSignIn(email, password);
    } catch {
      // Error is set in context
    }
    setSubmitting(false);
  };

  const onSignUp = async (e) => {
    e.preventDefault();
    setLocalError('');
    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      await handleSignUp(email, password);
    } catch {
      // Error is set in context
    }
    setSubmitting(false);
  };

  const onConfirm = async (e) => {
    e.preventDefault();
    setLocalError('');
    setSubmitting(true);
    try {
      await handleConfirmSignUp(code, password);
    } catch {
      // Error is set in context
    }
    setSubmitting(false);
  };

  const displayError = localError || error;

  // During code confirmation an accidental outside-tap must not close the
  // modal: closing wipes the held password, which kills the post-confirm
  // auto-sign-in and strands a verified-but-signed-out user. Explicit ✕ only.
  const overlayDismiss = authState === 'confirmSignUp' ? undefined : closeAuthModal;

  return (
    <div className="auth-modal-overlay" onClick={overlayDismiss}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close" onClick={closeAuthModal} aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="auth-logo">
          <div className="auth-logo-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
              <path
                d="M8 12l3 3 5-5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h1 className="auth-title">Molehill</h1>
        </div>

        {authState === 'signIn' && (
          <>
            <p className="auth-subtitle">The task manager for ADHD brains — sign in to sync across devices</p>
            <button
              type="button"
              className="auth-button auth-apple-button"
              onClick={handleAppleSignIn}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              Sign in with Apple
            </button>
            <div className="auth-divider">
              <span>or</span>
            </div>
            <form onSubmit={onSignIn} className="auth-form">
              <div className="auth-field">
                <label htmlFor="modal-email">Email</label>
                <input
                  id="modal-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                />
              </div>
              <div className="auth-field">
                <label htmlFor="modal-password">Password</label>
                <input
                  id="modal-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
              </div>
              {displayError && <p className="auth-error">{displayError}</p>}
              <button type="submit" className="auth-button" disabled={submitting}>
                {submitting ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
            <p className="auth-switch">
              Don't have an account?{' '}
              <button onClick={switchToSignUp} className="auth-link">
                Create account
              </button>
            </p>
          </>
        )}

        {authState === 'signUp' && (
          <>
            <p className="auth-subtitle">Create an account to sync your tasks</p>
            <form onSubmit={onSignUp} className="auth-form">
              <div className="auth-field">
                <label htmlFor="modal-signup-email">Email</label>
                <input
                  id="modal-signup-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                />
              </div>
              <div className="auth-field">
                <label htmlFor="modal-signup-password">Password</label>
                <input
                  id="modal-signup-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                />
              </div>
              <div className="auth-field">
                <label htmlFor="modal-confirm-password">Confirm Password</label>
                <input
                  id="modal-confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                />
              </div>
              {displayError && <p className="auth-error">{displayError}</p>}
              <button type="submit" className="auth-button" disabled={submitting}>
                {submitting ? 'Creating account...' : 'Create Account'}
              </button>
            </form>
            <p className="auth-switch">
              Already have an account?{' '}
              <button onClick={switchToSignIn} className="auth-link">
                Sign in
              </button>
            </p>
          </>
        )}

        {authState === 'confirmSignUp' && (
          <>
            <p className="auth-subtitle">Check your email for a verification code</p>
            <form onSubmit={onConfirm} className="auth-form">
              <div className="auth-field">
                <label htmlFor="modal-code">Verification Code</label>
                <input
                  id="modal-code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  required
                  autoFocus
                  maxLength={6}
                  className="auth-code-input"
                />
              </div>
              {displayError && <p className="auth-error">{displayError}</p>}
              <button type="submit" className="auth-button" disabled={submitting}>
                {submitting ? 'Verifying...' : 'Verify'}
              </button>
            </form>
            <p className="auth-switch">
              Didn't receive a code?{' '}
              <button onClick={handleResendCode} className="auth-link">
                Resend code
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
